/**
 * Cloudinary on-the-fly transformation helper.
 *
 * Appends format/quality/width directives to any Cloudinary URL so customers
 * on mobile get a 70%-smaller WebP/AVIF instead of the full original JPG.
 *
 * - f_auto: Cloudinary picks the best format (AVIF on Safari 16+, WebP elsewhere)
 * - q_auto: smart-compression to the lowest perceptual quality
 * - w_<n>:  capped width in CSS pixels — Cloudinary delivers a 2x DPR file
 *           when the browser asks via srcset
 *
 * Non-Cloudinary URLs (existing external images) are returned untouched.
 */

const CLOUDINARY_PATTERN = /\/image\/upload\//;

/**
 * Insert a transformation chain into a Cloudinary URL.
 * @param {string} url - Cloudinary `secure_url` or any other URL
 * @param {object} opts
 * @param {number} [opts.w] - target width in CSS pixels (we 2x it for the @2x file)
 * @param {boolean} [opts.crop=true] - crop to fill instead of letterbox
 * @returns {string}
 */
export const optimizedImage = (url, { w, crop = true } = {}) => {
    if (!url || typeof url !== "string") return url;
    if (!CLOUDINARY_PATTERN.test(url)) return url;
    // If the URL already has transformations (someone hand-edited), keep them.
    if (url.match(/\/upload\/[a-z]_/)) return url;
    const tx = ["f_auto", "q_auto"];
    if (w) tx.push(`w_${Math.round(w * 2)}`); // 2x for retina
    if (w && crop) tx.push("c_fill");
    const chain = tx.join(",");
    return url.replace("/image/upload/", `/image/upload/${chain}/`);
};

/**
 * Build a responsive srcset for retina-aware loading.
 * @param {string} url
 * @param {number} w - 1x CSS-pixel width
 */
export const optimizedSrcSet = (url, w) => {
    if (!url || !CLOUDINARY_PATTERN.test(url)) return undefined;
    if (url.match(/\/upload\/[a-z]_/)) return undefined;
    const one = url.replace("/image/upload/", `/image/upload/f_auto,q_auto,c_fill,w_${w}/`);
    const two = url.replace("/image/upload/", `/image/upload/f_auto,q_auto,c_fill,w_${w * 2}/`);
    return `${one} 1x, ${two} 2x`;
};
